import {LanguageService} from "../service/LanguageService";
import React from "react";
import {usage_types_values} from "./Constants";
import {getDateString, toDateString} from "./TimeUtil";

const urlRegex = RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{0,2}[a-z0-9ı]+)*\.[a-z]{2,7}(:[0-9]{1,5})?(\/.*)?$/
);

const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const decimalRegex = RegExp(/^[0-9]*\.?[0-9]?[0-9]?$/);

//pick some fields from object
export const pick = (...props) => o => props.reduce((a, e) => ({ ...a, [e]: o[e] }), {});

export const getLastPrice = (prices) => {
    return prices && prices.length!==0 ? prices.reduce((a,b) => {
        return new Date(a.created_at) > new Date(b.created_at) ? a : b;
    }) : null;
};

export const getDatePrice = (prices, p_dateString) => {
    //TODO:getDateString  toDateString ile değiştirilecek. performans sorunu düzeltilince.
    const datePrices = prices?.filter(p=> getDateString(p.created_at) === p_dateString);
    if (datePrices.length !== 0)
        return datePrices[datePrices.length -1];
    else
        return null;
};

// Ad/analytics click ids that carry no product information (Google Shopping srsltid,
// Google Ads gclid, ...). Kept in sync with URLUtil.TRACKING_PARAMS on the backend.
const trackingParams = new Set(["srsltid", "gclid", "gclsrc", "gbraid", "wbraid", "dclid",
    "fbclid", "msclkid", "yclid", "igshid", "mc_cid", "mc_eid"]);
const trackingParamPrefixes = ["utm_"];

export const stripTrackingParams = (url) => {

    const trimmed = (url || "").trim();
    const queryIndex = trimmed.indexOf("?");
    if (queryIndex === -1)
        return trimmed;

    const hashIndex = trimmed.indexOf("#", queryIndex);
    const base = trimmed.slice(0, queryIndex);
    const hash = hashIndex === -1 ? "" : trimmed.slice(hashIndex);
    const query = trimmed.slice(queryIndex + 1, hashIndex === -1 ? undefined : hashIndex);

    const kept = query.split("&").filter(part => {
        if (!part)
            return false;
        const name = part.split("=")[0].toLowerCase();
        return !trackingParams.has(name) && !trackingParamPrefixes.some(prefix => name.startsWith(prefix));
    });

    return base + (kept.length > 0 ? "?" + kept.join("&") : "") + hash;
};

export const urlValidator =(url, t)=>{

    let error = "";
    url = url.toLowerCase();
    if (!(url.startsWith("http://") || url.startsWith("https://")))
        error = t('validations.regex_url');
    else {
        error = urlRegex.test(url) ? "" : t('validations.invalid_url');
    }

    return error;
};

export const emailValidator = (email, t) => {
    return emailRegex.test(email) ? "" : t('validations.invalid_email');
};

export const isNumeric = (value) => {
    return !isNaN(parseFloat(value)) && isFinite(value);
};

export const formValid = (formErrors) => {

    let isValid = true;

    if (formErrors !== null)
    {
        // validate form errors being empty
        Object.values(formErrors).forEach(val => {
            if (val.length > 0) isValid = false;
        });
    }
    return isValid;
};


const urlPath = (url) => {
    const withoutProtocol = (url || "").replace(/^https?:\/\//i, "");
    const slashIndex = withoutProtocol.indexOf("/");
    if (slashIndex === -1)
        return "";
    return withoutProtocol.slice(slashIndex).split("?")[0].split("#")[0].toLowerCase();
};

// Host plus the marketplace product-id token, which stays the same across vendor
// and parameter variants of one product page; falls back to host plus path.
const productKey = (url) => {
    const host = (getHostName(url) || "").toLowerCase();
    const path = urlPath(url);
    const patterns = [[/-p-([a-z0-9]+)/g, true], [/-pm-([a-z0-9]+)/g, true],
                      [/\/dp\/([a-z0-9]{10})/g, false], [/\/p-([a-z0-9]+)/g, true]];
    for (const [pattern, requireDigit] of patterns) {
        const matches = [];
        let match;
        while ((match = pattern.exec(path)) !== null)
            matches.push(match[1]);
        const ids = requireDigit ? matches.filter(id => /\d/.test(id)) : matches;
        if (ids.length > 0)
            return host + "|" + ids[ids.length - 1];   // the product id comes last by convention
    }
    return host + "|" + path.replace(/\/+$/, "");
};

// True when two URLs address the same product page of the same host despite
// vendor-slug or query differences -- a marketplace seller row is its listing's
// URL plus a seller parameter, so this is what ties a seller to its listing.
// Mirrors URLUtil.same_product on the backend, which decides the same grouping
// when a delete is actually applied; keep the two in step.
export const isSameProductUrl = (urlA, urlB, minLength = 15) => {
    if (!urlA || !urlB)
        return false;
    const keyA = productKey(urlA);
    const keyB = productKey(urlB);
    if (keyA && keyA === keyB)
        return true;
    const hostA = getHostName(urlA);
    const hostB = getHostName(urlB);
    if (!hostA || hostA !== hostB)
        return false;
    // A vendor-slug URL carries the canonical path inside it (idefix case).
    const pathA = urlPath(urlA).replace(/\/+$/, "");
    const pathB = urlPath(urlB).replace(/\/+$/, "");
    const shorter = pathA.length <= pathB.length ? pathA : pathB;
    const longer = pathA.length <= pathB.length ? pathB : pathA;
    if (shorter.length < minLength)
        return false;
    const index = longer.indexOf(shorter);
    if (index === -1)
        return false;
    const end = index + shorter.length;
    return end === longer.length || ["/", "?", "#"].includes(longer[end]);
};

export const getHostName = (url) => {
    if (url == null)
        return null
    if (!(url.startsWith("http://") || url.startsWith("https://")))
        url = "https://" + url;
    let match = url.match(/:\/\/(www[0-9]?\.)?(.[^/:]+)/i);
    if (match != null && match.length > 2 && typeof match[2] === 'string' && match[2].length > 0) {
        return match[2];
    }
    else {
        return null;
    }
};


export const currencyFormatter = new Intl.NumberFormat(LanguageService.getLanguage(), {
    style: 'decimal', minimumFractionDigits : 2, maximumFractionDigits: 2
});

export const formatDecimal = (value) => {
    if (value === undefined || value === null || value === "" || value === "-")
        return "-";
    return currencyFormatter.format(value);
};

// The seller lists the product in stock without a price ("Contact for Price"): value 0
// or no value at all. It sells the product, so it stays visible, but it is not a price.
export const isPriceNotListed = (price) => !!price && price.stock > 0 && !(Number(price.value) > 0);

export const formatCurrency = (value, currency) => {
    if (value === undefined || value === null || value === "" || value === "-")
        return "-";

    if (currency === "$")
        return currency + currencyFormatter.format(value);
    return <span className="p-text-nowrap p-text-right"><span>{currencyFormatter.format(value)}</span>&nbsp;<sup style={{fontSize:'.7em'}}>{" " + (currency ? currency:"")}</sup></span>;
};

export const formatPercentage = (value) => {
    if (value === undefined || value === null || value === "" || value === "-")
        return "-";

        return  value + ' %';
};



export const formatCurrencyText = (value, currency) => {
    if (value === undefined || value === null || value === "" || value === "-")
        return "-";

    if (currency === "$")
        return currency + currencyFormatter.format(value);
    return currencyFormatter.format(value) +" " + (currency ? currency:"");
};


export const loggedInEmail = () => {return localStorage.getItem("loggedInEmail") ? localStorage.getItem("loggedInEmail") : null};
export const user = () => {return localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null};
export const subscription = () => (localStorage.getItem("subscription") ? JSON.parse(localStorage.getItem("subscription")) : null);
export const next_subscription = () => (localStorage.getItem("next_subscription") ? JSON.parse(localStorage.getItem("next_subscription")) : null);
export const isAdmin = ()=> (user() ? user().is_admin : false);
export const isManager = ()=> (user() ? user().is_manager : false);
// EFS Elektronik customer-specific features (tiered-discount columns + own-price
// edit on the product detail page) are gated to this single user.
export const EFS_ELEKTRONIK_USER_ID = '4633287a-b67b-478f-bb93-411b53fd0290';
export const isEfsElektronikUser = ()=> (user() ? user().id === EFS_ELEKTRONIK_USER_ID : false);
// Typing competitor prices by hand: admins, and the customers an admin enabled it for
// (user.settings.manual_competitor_prices_enabled; pricerest enforces the same rule).
export const canEnterManualPrices = ()=> isAdmin() || !!(user()?.settings?.manual_competitor_prices_enabled);
export const isUserManaged = ()=> (user() ? user().managed_by !== null : false);
export const isSubProfile = ()=> (user() ? loggedInEmail().toLowerCase() !== user().email.toLowerCase() : false);
export const isMainProfile = ()=> (user() ? loggedInEmail().toLowerCase() === user().email.toLowerCase() : false);
export const isCampaignSeller = (campaign)=> (campaign?.settings?.usage_type ? campaign?.settings?.usage_type === usage_types_values[0].value : (user() && user().usage_type && user().usage_type === usage_types_values[0].value));
export const isCampaignManufacturer = (campaign)=> (campaign?.settings?.usage_type ? campaign?.settings?.usage_type === usage_types_values[1].value : (user() && user().usage_type && user().usage_type === usage_types_values[1].value));
export const isCampaignMarketSearch = (campaign)=> (campaign?.settings?.usage_type ? campaign?.settings?.usage_type === usage_types_values[2].value : (user() && user().usage_type && user().usage_type === usage_types_values[2].value));
export const isCampaignDropShipping = (campaign)=> (campaign?.settings?.usage_type ? campaign?.settings?.usage_type === usage_types_values[3].value : (user() && user().usage_type && user().usage_type === usage_types_values[3].value));
export const isPriceViolationTabShown = (campaign)=> (campaign?.settings?.show_price_violation_tab ? campaign?.settings?.show_price_violation_tab : false);
export const isShippingPricesHandledSeparately = (campaign)=> (campaign?.settings?.shipping_prices_handled_separately ? !campaign?.settings?.shipping_prices_handled_separately : true);
export const userId = () => (user() ? user().id : "");

export const getKeyValue = (e) => {
    if (e.target) {
        if (e.target.type) {
            if (e.target.type === "checkbox")
                return {key: e.target.name, value: e.target.checked};
            else
                return {key: e.target.name, value: e.target.value};
        } else
            return {key: e.target.name, value: e.target.value};
    }
    return {key: null, value: null};
};

export const getLinkFromUrl = (url) => {
    return url ? <a target="_blank" rel="noopener noreferrer" href={url}>{url}
    </a> : null;
};

// A hold is rewritten on every ikas send that resolves the product and cleared
// only by such a send, so one that has not been confirmed for a week is more
// likely stale (the duplicate was removed) than true.
const PRICE_SEND_HOLD_MAX_AGE_DAYS = 7;

// The backend sends naive UTC timestamps on some paths; read them as UTC.
const parseUtcTimestamp = (value) => {
    if (!value) return null;
    const text = String(value);
    const date = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : text + 'Z');
    return isNaN(date.getTime()) ? null : date;
};

export const getActivePriceSendHold = (product) => {
    const hold = product && product.price_send_hold;
    if (!hold) return null;
    const checkedAt = parseUtcTimestamp(hold.checked_at);
    if (checkedAt && Date.now() - checkedAt.getTime() > PRICE_SEND_HOLD_MAX_AGE_DAYS * 24 * 60 * 60 * 1000) {
        return null;
    }
    return {...hold, checkedAt};
};

export const getProductDetailLink = (id, label, competitorUrl, inline) => {
    const competitorUrlParam = competitorUrl ? "&highlight_competitor_url=" + encodeURIComponent(competitorUrl) : "";

    return <a target="_blank" rel="noopener noreferrer" href={"/#/productdetail?id="+id+competitorUrlParam}>
        <h6 style={inline ? {display: "inline", marginRight: ".75em"} : null}>{label}</h6>
    </a>;
};

export const  toTitleCase = (phrase) => {
    return phrase
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const getLanguageImage = (language) =>{
    return <img src={"assets/layout/flags/"+language+".svg"}  height="28" width="28" alt={language}/>;
};

export const isPreventSelect = (e) => {
    let result = false;

    while (e)
    {
        if (e.className && e.className.indexOf("preventSelect") !== -1) {
            result = true;
            break;
        }
        e = e.parentNode;
    }
    return result;
}

export const exportFile = (response, fileName) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); //or any other extension
    document.body.appendChild(link);
    link.click();
};

export const populateInactiveSellers = (campaignData) => {
    campaignData.forEach(item => {
        item.new_seller_automatically_activated = item.settings && item.settings.new_seller_automatically_activated !== null && item.settings.new_seller_automatically_activated !== undefined ? item.settings.new_seller_automatically_activated : true
        item.inactive_sellers = new Set()
        item.active_sellers = new Set()
        item?.competitors?.forEach(competitor => {
            if (competitor?.sellers?.length === 0) {
                let hostname = getHostName(competitor.url)
                if (item.new_seller_automatically_activated) {
                    if (competitor.is_active !== undefined && !competitor.is_active){
                        item.inactive_sellers.add(hostname)
                    }
                } else {
                    if (competitor.is_active) {
                        item.active_sellers.add(hostname)
                    }
                }
            } else {
                competitor?.sellers?.forEach(seller => {
                    if (item.new_seller_automatically_activated) {
                        if (seller.is_active !== undefined && !seller.is_active){
                            item.inactive_sellers.add(seller.seller_name)
                        }
                    } else {
                        if (seller.is_active) {
                            item.active_sellers.add(seller.seller_name)
                        }
                    }
                })
            }

        })
    })
}
